import React, { useState, useEffect, useRef, useCallback } from "react";
import { useAuth } from "../../providers/AuthContext";
import Cookies from "js-cookie";
import { API_URL } from "../../config";
import { useMeetings } from "../../providers/MeetingsContext";

const Meetings = () => {
  // Use meetings from context
  const {
    meetings: contextMeetings,
    loading: contextLoading,
    error: contextError,
    setLoading: setContextLoading,
    setError: setContextError,
    setMeetingsData,
    updateMeetings,
    addMeeting,
    updateMeeting,
    removeMeeting,
    loadFromCache,
  } = useMeetings();

  const [progress, setProgress] = useState(0);
  const [connectionStatus, setConnectionStatus] = useState("disconnected");
  const { user } = useAuth();

  // WebSocket ref and control flags
  const wsRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const keepAliveIntervalRef = useRef(null);
  const reconnectAttemptsRef = useRef(0);
  const isConnectingRef = useRef(false);
  const mountedRef = useRef(true);

  // Progress Circle Component
  const ProgressCircle = ({ progress }) => {
    const radius = 10;
    const strokeWidth = 2;
    const normalizedRadius = radius - strokeWidth * 2;
    const circumference = normalizedRadius * 2 * Math.PI;
    const strokeDasharray = `${circumference} ${circumference}`;
    const strokeDashoffset = circumference - (progress / 100) * circumference;

    return (
      <div className="flex items-center gap-2">
        <svg
          height={radius * 2}
          width={radius * 2}
          className="transform -rotate-90"
        >
          <circle
            stroke="#e5e7eb"
            fill="transparent"
            strokeWidth={strokeWidth}
            r={normalizedRadius}
            cx={radius}
            cy={radius}
          />
          <circle
            stroke="#1F9BFF"
            fill="transparent"
            strokeWidth={strokeWidth}
            strokeDasharray={strokeDasharray}
            style={{ strokeDashoffset }}
            strokeLinecap="round"
            r={normalizedRadius}
            cx={radius}
            cy={radius}
          />
        </svg>
        <span className="text-sm text-gray-600">
          Syncing... {Math.round(progress)}%
        </span>
      </div>
    );
  };

  // Clean up function
  const cleanup = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
    if (keepAliveIntervalRef.current) {
      clearInterval(keepAliveIntervalRef.current);
      keepAliveIntervalRef.current = null;
    }
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      wsRef.current.close(1000, "Component cleanup");
    }
    wsRef.current = null;
    isConnectingRef.current = false;
  }, []);

  // Fetch meetings from API - called on every page load
  const fetchMeetingsFromAPI = useCallback(async () => {
    if (!user?.id) {
      console.log("❌ No user ID available for API call");
      return;
    }

    const token = Cookies.get("access_token");
    if (!token) {
      console.log("❌ No access token available for API call");
      setContextError("Please sign in to view meetings");
      return;
    }

    try {
      setContextLoading(true);
      setContextError(null);
      console.log("🔄 Fetching meetings from /today API...");

      const response = await fetch(`${API_URL}/calendar/${user.id}/today`, {
        method: "GET",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      console.log("📅 API response received:", data);

      // Process the API response
      let eventsToDisplay = [];

      if (data.events && Array.isArray(data.events)) {
        eventsToDisplay = data.events;
      } else if (data.meetings && Array.isArray(data.meetings)) {
        eventsToDisplay = data.meetings;
      } else if (Array.isArray(data)) {
        eventsToDisplay = data;
      }

      // Update context with fetched meetings
      setMeetingsData(eventsToDisplay, "api");
      setContextLoading(false);

      // Dispatch custom event for other components
      window.dispatchEvent(
        new CustomEvent("meetings_updated", {
          detail: { meetings: eventsToDisplay },
        })
      );

      console.log(`✅ Loaded ${eventsToDisplay.length} meetings from API`);
    } catch (error) {
      console.error("❌ Error fetching meetings from API:", error);
      setContextError("Failed to load meetings");
      setContextLoading(false);

      // Try to use cached data as fallback
      const cacheLoaded = loadFromCache();
      if (cacheLoaded) {
        setContextError(null);
        console.log("📦 Using cached meetings as fallback");
      }
    }
  }, [
    user?.id,
    setContextLoading,
    setContextError,
    setMeetingsData,
    loadFromCache,
  ]);

  // Build WebSocket URL
  const getWebSocketUrl = useCallback(() => {
    const token = Cookies.get("access_token");

    if (!user?.id || !token) {
      console.log("❌ Missing data for WebSocket URL:", {
        hasUserId: !!user?.id,
        hasToken: !!token,
      });
      return null;
    }

    const baseUrl =
      import.meta.env.VITE_API_URL_PROD || "https://backend.efficia.ai";

    const wsUrl = `${baseUrl
      .replace("https://", "wss://")
      .replace("http://", "ws://")}/ws/calendar/${user.id}/?token=${token}`;

    console.log(
      "🔗 Calendar WebSocket URL:",
      wsUrl.replace(token, "[TOKEN_HIDDEN]")
    );
    return wsUrl;
  }, [user]);

  // Handle incoming WebSocket messages
  const handleMessage = useCallback(
    (event) => {
      if (!mountedRef.current) return;

      try {
        const data = JSON.parse(event.data);
        console.log("📨 Calendar WebSocket message received:", data.type, data);

        switch (data.type) {
          case "connection_established":
            console.log("✅ Calendar connection established by server");
            setConnectionStatus("connected");
            setContextError(null);

            // Send calendar_notification action after connection
            const calendarNotificationRequest = {
              action: "reschedule_meetings",
            };

            console.log(
              "📤 Sending calendar_notification request:",
              calendarNotificationRequest
            );

            setTimeout(() => {
              if (wsRef.current?.readyState === WebSocket.OPEN) {
                wsRef.current.send(JSON.stringify(calendarNotificationRequest));
              }
            }, 100);
            break;

          case "calendar_notification":
            console.log("📅 Calendar notification received");
            break;

          case "handle_reschedule":
            console.log("🔄 Handle reschedule triggered, starting sync...");
            setProgress(0);
            break;

          case "calendar_sync_progress":
            const syncProgressValue = data.progress || 0;
            console.log("📊 Calendar sync progress:", syncProgressValue);
            setProgress(syncProgressValue);
            break;

          case "calendar_sync_complete":
          case "reschedule_complete":
            console.log("✅ Calendar sync/reschedule complete");
            setProgress(100);

            // Process the completed data if available
            if (data.events && Array.isArray(data.events)) {
              updateMeetings(data.events, "websocket");

              // Dispatch custom event for other components
              window.dispatchEvent(
                new CustomEvent("meetings_updated", {
                  detail: { meetings: data.events },
                })
              );

              console.log(
                `✅ Updated ${data.events.length} meetings from WebSocket`
              );
            }

            setContextError(null);

            // Reset progress after a short delay
            setTimeout(() => {
              setProgress(0);
            }, 1000);
            break;

          case "meetings_data":
          case "meeting_update":
          case "meetings_update":
            let eventsToDisplay = [];

            if (data.events && Array.isArray(data.events)) {
              eventsToDisplay = data.events;
            } else if (data.data && Array.isArray(data.data)) {
              eventsToDisplay = data.data;
            } else if (Array.isArray(data)) {
              eventsToDisplay = data;
            }

            if (eventsToDisplay.length > 0) {
              updateMeetings(eventsToDisplay, "websocket");

              window.dispatchEvent(
                new CustomEvent("meetings_updated", {
                  detail: { meetings: eventsToDisplay },
                })
              );

              console.log(`✅ Updated meetings via WebSocket`);
            }

            setContextError(null);
            break;

          case "new_meeting":
            // Handle single new meeting
            if (data.meeting) {
              addMeeting(data.meeting);

              window.dispatchEvent(
                new CustomEvent("meetings_updated", {
                  detail: { meetings: [data.meeting] },
                })
              );

              console.log(`✅ Added new meeting: ${data.meeting.summary}`);

              // Show notification for new meeting
              if (Notification.permission === "granted") {
                new Notification(`New meeting: ${data.meeting.summary}`, {
                  body: `${formatTime(
                    data.meeting.start?.dateTime || data.meeting.start?.date
                  )}`,
                  icon: "/meet.png",
                });
              }
            }
            break;

          case "meeting_deleted":
            // Handle meeting deletion
            if (data.meeting_id) {
              removeMeeting(data.meeting_id);

              window.dispatchEvent(
                new CustomEvent("meetings_updated", {
                  detail: { meetingId: data.meeting_id, action: "deleted" },
                })
              );

              console.log(`✅ Removed meeting with ID: ${data.meeting_id}`);
            }
            break;

          case "meeting_updated":
            // Handle meeting updates
            if (data.meeting) {
              updateMeeting(data.meeting);

              window.dispatchEvent(
                new CustomEvent("meetings_updated", {
                  detail: { meetings: [data.meeting], action: "updated" },
                })
              );

              console.log(`✅ Updated meeting: ${data.meeting.summary}`);
            }
            break;

          case "error":
            console.error("❌ Server error:", data.message);

            // Don't show certain errors on the UI - only log them
            const ignoredErrors = [
              "Unknown action",
              "Action not recognized",
              "Invalid action",
              "Unhandled action",
            ];

            const shouldShowError = !ignoredErrors.some((ignoredError) =>
              data.message?.toLowerCase().includes(ignoredError.toLowerCase())
            );

            if (shouldShowError) {
              setContextError(data.message || "Server error");
            } else {
              console.log(
                "🤫 Suppressed UI error (logged only):",
                data.message
              );
            }

            setProgress(0);
            break;

          default:
            console.log("❓ Unknown message type:", data.type);
        }
      } catch (error) {
        console.error("💥 Error parsing calendar message:", error);
      }
    },
    [updateMeetings, addMeeting, updateMeeting, removeMeeting, setContextError]
  );

  // WebSocket connection function
  const connect = useCallback(() => {
    if (!mountedRef.current || isConnectingRef.current) {
      return;
    }

    const wsUrl = getWebSocketUrl();
    if (!wsUrl) {
      console.log("❌ Cannot connect: missing user data");
      return;
    }

    // Don't reconnect if already connected
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      console.log("✅ Already connected to calendar WebSocket");
      return;
    }

    console.log("🔌 Connecting to Calendar WebSocket...");
    setConnectionStatus("connecting");
    isConnectingRef.current = true;

    try {
      // Clean up existing connection
      if (wsRef.current) {
        wsRef.current.close();
      }

      wsRef.current = new WebSocket(wsUrl);

      // Connection timeout
      const connectionTimeout = setTimeout(() => {
        if (
          isConnectingRef.current &&
          wsRef.current?.readyState === WebSocket.CONNECTING
        ) {
          console.log("⏰ Calendar WebSocket connection timeout");
          wsRef.current.close();
          setConnectionStatus("error");
          isConnectingRef.current = false;
        }
      }, 10000);

      wsRef.current.onopen = () => {
        clearTimeout(connectionTimeout);
        if (!mountedRef.current) return;

        console.log("🎉 Calendar WebSocket connected successfully!");
        setConnectionStatus("connecting");
        reconnectAttemptsRef.current = 0;
        isConnectingRef.current = false;
      };

      wsRef.current.onmessage = handleMessage;

      wsRef.current.onerror = (error) => {
        clearTimeout(connectionTimeout);
        if (!mountedRef.current) return;

        console.error("💥 Calendar WebSocket error:", error);
        setConnectionStatus("error");
        isConnectingRef.current = false;
      };

      wsRef.current.onclose = (event) => {
        clearTimeout(connectionTimeout);
        if (keepAliveIntervalRef.current) {
          clearInterval(keepAliveIntervalRef.current);
          keepAliveIntervalRef.current = null;
        }
        if (!mountedRef.current) return;

        console.log(
          "🔌 Calendar WebSocket closed with code:",
          event.code,
          "reason:",
          event.reason
        );
        setConnectionStatus("disconnected");
        isConnectingRef.current = false;

        // Auto-reconnect logic
        if (event.code !== 1000 && reconnectAttemptsRef.current < 5) {
          const delay = Math.min(
            1000 * Math.pow(2, reconnectAttemptsRef.current),
            10000
          );
          console.log(
            `🔄 Calendar WebSocket reconnecting in ${delay}ms (attempt ${
              reconnectAttemptsRef.current + 1
            }/5)`
          );

          reconnectTimeoutRef.current = setTimeout(() => {
            if (mountedRef.current) {
              reconnectAttemptsRef.current++;
              connect();
            }
          }, delay);
        } else if (event.code !== 1000) {
          console.log(
            "❌ Max calendar WebSocket reconnection attempts reached"
          );
        }
      };
    } catch (error) {
      console.error("💥 Error creating calendar WebSocket:", error);
      setConnectionStatus("error");
      isConnectingRef.current = false;
    }
  }, [getWebSocketUrl, user, handleMessage]);

  // Main effect to handle API fetch and WebSocket connection
  useEffect(() => {
    mountedRef.current = true;

    const handlePageLoad = async () => {
      const token = Cookies.get("access_token");

      if (user?.id && token) {
        console.log("✅ User authenticated, fetching meetings...");

        // Always fetch from API on page load
        await fetchMeetingsFromAPI();

        // Connect WebSocket for real-time updates
        setTimeout(() => {
          if (mountedRef.current && user?.id && Cookies.get("access_token")) {
            connect();
          }
        }, 500);
      } else if (user === null) {
        console.log("❌ User not authenticated");
        setContextLoading(false);
        setContextError("Please sign in to view meetings");
      } else if (user && (!user.id || !token)) {
        console.log("⚠️ User data incomplete");
        setContextLoading(false);
        setContextError("User authentication incomplete");
      } else {
        console.log("⏳ Waiting for user data...");
        setContextLoading(true);
      }
    };

    handlePageLoad();

    return () => {
      console.log("🧹 Calendar component unmounting, cleaning up...");
      mountedRef.current = false;
      cleanup();
    };
  }, [
    user,
    fetchMeetingsFromAPI,
    connect,
    cleanup,
    setContextLoading,
    setContextError,
  ]);

  // Format time helper
  const formatTime = (dateTime) => {
    if (!dateTime) return "";
    try {
      const date = new Date(dateTime);
      if (isNaN(date.getTime())) return "";
      return date.toLocaleTimeString([], {
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      });
    } catch (err) {
      console.error("Error formatting meeting time:", err);
      return "";
    }
  };

  // Connection status helpers
  const shouldShowConnectionStatus = () => {
    return progress > 0;
  };

  const getStatusColor = () => {
    switch (connectionStatus) {
      case "connected":
        return "text-green-500";
      case "connecting":
        return "text-blue-500";
      case "error":
        return "text-red-500";
      default:
        return "text-gray-500";
    }
  };

  const getStatusText = () => {
    switch (connectionStatus) {
      case "connected":
        return "Live";
      case "connecting":
        return "Connecting...";
      case "error":
        return "Error";
      default:
        return "Offline";
    }
  };

  const getStatusIcon = () => {
    switch (connectionStatus) {
      case "connected":
        return "bg-green-500";
      case "connecting":
        return "bg-blue-500 animate-pulse";
      case "error":
        return "bg-red-500";
      default:
        return "bg-gray-500";
    }
  };

  // Use meetings from context
  const todayMeetings = contextMeetings;
  const loading = contextLoading;
  const error = contextError;

  return (
    <div className="bg-[#FCFCFC] min-h-[15rem] p-4 w-[49%] flex flex-col rounded-lg shadow-sm shadow-gray-300 h-full">
      {/* Header */}
      <div className="flex justify-between items-center mb-3 sm:mb-2 flex-shrink-0">
        <div>
          <h2 className="text-lg sm:text-base font-extrabold text-gray-600">
            Today's Meetings
          </h2>
          <span className="text-[12px] sm:text-[12px] text-gray-500">
            {todayMeetings.length > 0
              ? `You have ${todayMeetings.length} meeting${
                  todayMeetings.length !== 1 ? "s" : ""
                } today`
              : "No meetings scheduled for today"}
          </span>
        </div>

        {/* Connection status and progress - only show during sync */}
        <div className="flex flex-col items-end text-xs">
          {progress > 0 ? (
            <ProgressCircle progress={progress} />
          ) : shouldShowConnectionStatus() ? (
            <div className="flex items-center">
              <div
                className={`w-2 h-2 rounded-full mr-1 ${getStatusIcon()}`}
              ></div>
              <span className={getStatusColor()}>{getStatusText()}</span>
            </div>
          ) : null}
        </div>
      </div>

      {/* Content area */}
      <div className="flex-grow overflow-y-auto">
        {loading ? (
          <div className="flex justify-center items-center h-32">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500"></div>
          </div>
        ) : error ? (
          <div className="flex flex-col justify-center items-center h-32 text-gray-500">
            <p className="text-center text-sm mb-2">{error}</p>
          </div>
        ) : todayMeetings.length === 0 ? (
          <div className="flex flex-col justify-center items-center h-32 text-gray-500">
            <p className="text-center">No meetings scheduled for today</p>
          </div>
        ) : (
          <div className="space-y-2">
            {todayMeetings.map((meeting, index) => (
              <div
                key={meeting.id || index}
                className="border-b border-gray-100 pb-2"
              >
                <div className="flex items-center justify-between p-2 hover:bg-gray-50 rounded">
                  <div className="flex flex-col">
                    <div className="text-xs font-medium text-black">
                      {formatTime(
                        meeting.start?.dateTime || meeting.start?.date
                      )}
                      {" — "}
                      {formatTime(meeting.end?.dateTime || meeting.end?.date)}
                    </div>
                    <p className="text-gray-600 text-sm font-semibold">
                      {meeting.summary || "Untitled Event"}
                    </p>
                    {meeting.attendees && meeting.attendees.length > 0 && (
                      <div className="text-xs text-gray-500 mt-1">
                        {meeting.attendees.length} attendee
                        {meeting.attendees.length !== 1 ? "s" : ""}
                      </div>
                    )}
                  </div>

                  {/* Handle both hangoutLink and conferenceData */}
                  {meeting.hangoutLink ||
                  meeting.conferenceData?.entryPoints?.[0]?.uri ||
                  meeting.conferenceData?.link ? (
                    <a
                      href={
                        meeting.hangoutLink ||
                        meeting.conferenceData?.entryPoints?.[0]?.uri ||
                        meeting.conferenceData?.link
                      }
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex-shrink-0"
                    >
                      <img
                        src="/meet.png"
                        alt="Join Meeting"
                        className="w-8 h-8 object-contain hover:scale-110 transition-transform"
                        onError={(e) => {
                          e.target.src = "/meet.png";
                          e.target.onerror = null;
                        }}
                      />
                    </a>
                  ) : (
                    <img
                      src="/meet.png"
                      alt="Meeting"
                      className="w-8 h-8 object-contain opacity-40"
                      onError={(e) => {
                        e.target.src = "/meet.png";
                        e.target.onerror = null;
                      }}
                    />
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="mt-auto pt-3 flex-shrink-0">
        <button
          className="w-full bg-[#1F9BFF1A] text-[#1F9BFF] hover:bg-[#1F9BFF] hover:text-white font-medium py-2 px-4 rounded transition-all duration-200"
          onClick={() => (window.location.href = "/calendar")}
        >
          View Calendar
        </button>
      </div>
    </div>
  );
};

export default Meetings;
